import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useRect } from 'react-use-rect';
import {
  APP_CLASSNAME_PREFIX,
  DEFAULT_VIDEO_HEIGHT,
  DEFAULT_VIDEO_WIDTH,
  SUPPORT_PROVIDERS
} from '../../../constants';
import { getVideoURL } from '../../helpers';
import { Player } from './Player';

export const Background = ({ data, active, cloned = false, onReady }) => {
  const {
    imageEffect,
    backgroundMediaImage,
    backgroundMediaVideo,
    backgroundMediaType
  } = data;

  const componentRef = useRef(null);
  const onReadyRef = useRef(onReady);

  const [mediaDimensions, setMediaDimensions] = useState({
    width: 0,
    height: 0
  });
  const [containerWidth, setContainerWidth] = useState(0);
  const [measureRef] = useRect(({ width }) => setContainerWidth(width), {
    resize: true
  });

  const isImage = backgroundMediaType === 'image';
  const media = isImage ? backgroundMediaImage : backgroundMediaVideo;
  const fixedMedia = media?.originalValue || media?.url || media;
  const backgroundImage = fixedMedia ? `url(${fixedMedia})` : '';

  const type =
    SUPPORT_PROVIDERS.find((item) => item === media?.type) || 'image';

  const [hideBackground, setHideBackground] = useState(false);

  useEffect(() => {
    const { current: component } = componentRef;
    if (component && imageEffect === 'zoom') {
      setTimeout(() => {
        component.style.transform = active
          ? 'matrix(1.21,0,0,1.21,0,0)'
          : 'matrix(1,0,0,1,0,0)';
      });
    }
  }, [active, imageEffect]);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    const delay = backgroundMediaType === 'video' && active ? 1000 : 0;
    const timer = setTimeout(() => {
      setHideBackground(!active);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [active, backgroundMediaType]);

  useEffect(() => {
    if (isImage && backgroundMediaImage) {
      const { width, height, url } = backgroundMediaImage;

      if (!width || !height) {
        const image = new Image();
        image.src = url;

        const handleLoadImage = () => {
          setMediaDimensions({ width: image.width, height: image.height });
        };

        image.addEventListener('load', handleLoadImage);

        return () => {
          image.removeEventListener('load', handleLoadImage);
        };
      }

      setMediaDimensions({ width, height });
    } else {
      setMediaDimensions({
        width: DEFAULT_VIDEO_WIDTH,
        height: DEFAULT_VIDEO_HEIGHT
      });
    }
  }, [isImage, backgroundMediaImage]);

  useEffect(() => {
    const { width, height } = mediaDimensions;
    if (width && height && containerWidth) {
      const aspectRatio = height / width;
      onReadyRef.current({
        width: containerWidth,
        height: containerWidth * aspectRatio
      });
    } else {
      onReadyRef.current(null);
    }
  }, [mediaDimensions, containerWidth]);

  return (
    <div ref={measureRef}>
      {type === 'image' ? (
        <StyledImageBackground
          ref={componentRef}
          $backgroundImage={backgroundImage}
          $imageEffect={imageEffect}
        />
      ) : (
        <StyledVideoInner>
          <StyledVideoOuter $hideBackground={hideBackground}>
            {!cloned && (
              <Player
                playing={active}
                url={getVideoURL(media)}
                muted
                videoId={media.id ?? media.originalValue.split('/').pop()}
              />
            )}
          </StyledVideoOuter>
        </StyledVideoInner>
      )}
    </div>
  );
};

const StyledImageBackground = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-background-component`
})`
  background-position: center;
  background-size: cover;
  transform: matrix(1, 0, 0, 1, 0, 0);
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  backface-visibility: hidden;

  background-image: ${({ $backgroundImage }) => $backgroundImage};

  ${({ $imageEffect }) =>
    $imageEffect === 'zoom' &&
    css`
      transition: 20s linear transform;
    `}

  ${({ $imageEffect }) =>
    $imageEffect === 'parallax' &&
    css`
      transform: matrix(1.21, 0, 0, 1.21, 0, 0);
    `}
`;

const StyledVideoInner = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-background-inner`
})`
  width: 100%;
  height: 100%;
  background-color: black;
`;

const StyledVideoOuter = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-background-videoOuter`
})`
  position: absolute;
  left: 0;
  width: 100%;
  pointer-events: none;
  height: 300%;
  top: -100%;

  ${({ $hideBackground }) =>
    $hideBackground &&
    css`
      opacity: 0;
    `}
`;
